import * as Yup from 'yup';
import moment from 'moment';
import { YupId } from '../form/schema';
import {
    NURSE_DEPLOYMENT_TYPE_CANCELLATION,
    NURSE_DEPLOYMENT_TYPE_NURSE,
    NURSE_DEPLOYMENT_TYPE_PAUSE,
    NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED,
} from './types';

import { checkLegalGapDays } from './helpers';
import { isDateInThisFixationMonthOrEarlier } from '../../helpers/tools';

export const nurseDeploymentSchema = (
    nursedeployments,
    currentnursedeployment,
    latestFixation,
    isRoot,
    isBuHa
) => {
    // Once a month has been fixed (commission list already pulled for the agencies),
    // saving care_till/billing_from/billing_till into or before that month must fail -
    // re-checked here against the *live* value being saved, not just the originally
    // loaded record (see NurseDeploymentForm.js for why that distinction matters), and
    // enforced again server-side in NurseDeploymentRepository as a safety net.
    const fixationMonth = latestFixation?.fixationMonth;
    const isFixated = (value) => {
        if (isRoot || isBuHa) return false;
        return isDateInThisFixationMonthOrEarlier(value, fixationMonth);
    };
    const fixationMessage = fixationMonth
        ? `Der Monat ${new Date(fixationMonth).toLocaleString('de-DE', {
              month: 'long',
              year: 'numeric',
          })} befindet sich bereits in der Abrechnung mit den Agenturen. Bitte wenden Sie sich für Änderungen an die BuHa oder Teamleitung.`
        : '';
    // Only a field whose *already-saved* value wasn't already fixated can newly violate
    // this rule - one that already sat in/before the fixed month (e.g. a still-ongoing
    // deployment that merely *started* before it) is already disabled/read-only in the
    // form (see is_fixated / is_fixated_care_till in NurseDeploymentForm.js), so it can't
    // be what's actually changing; re-flagging it here would otherwise wrongly block
    // saving an unrelated, legitimate change just because that old, untouched field
    // happens to sit in the past.
    const testNotNewlyFixated = (originalValue) => (value) =>
        isFixated(originalValue) || !isFixated(value);

    return Yup.object().shape({
        careNeedId: YupId().required('Pflichtfeld'),
        customerContractId: YupId().required('Pflichtfeld'),
        customerContractEndAt: Yup.date(),
        agencyId: YupId().required('Pflichtfeld'),
        type: Yup.string()
            .oneOf([
                NURSE_DEPLOYMENT_TYPE_NURSE,
                NURSE_DEPLOYMENT_TYPE_PAUSE,
                NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED,
                NURSE_DEPLOYMENT_TYPE_CANCELLATION,
            ])
            .required('Pflichtfeld'),
        nurseId: YupId()
            .nullable()
            .when('type', {
                is: NURSE_DEPLOYMENT_TYPE_NURSE,
                then: YupId().nullable().required('Pflichtfeld'),
            }),
        pauseReason: Yup.string()
            .when('type', {
                is: (type) =>
                    type === NURSE_DEPLOYMENT_TYPE_PAUSE || type === NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED,
                then: Yup.string().required('Pflichtfeld'),
            }),
        careFrom: Yup.date()
            .nullable()
            .required('Pflichtfeld')
            .when('type', {
                is:  (type) => type === NURSE_DEPLOYMENT_TYPE_PAUSE || type === NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED,
                then:  Yup.date().required('Pflichtfeld')
                .test(
                    'is-date-in-use',
                    function (value) {
                        const { type, careFrom } = this.parent;
                        const result = checkLegalGapDays(nursedeployments, currentnursedeployment, careFrom, type);
                        if (!result.isValid) {
                            return this.createError({ message: result.message });
                        }
                        return true;
                    }
                ),
            })
            .when('type', {
                is:  (type) => type === NURSE_DEPLOYMENT_TYPE_CANCELLATION,
                then:  Yup.date().required('Pflichtfeld')
                .test(
                    'is-date-in-use',
                    function (value) {
                        const { type, careFrom } = this.parent;
                        const result = checkLegalGapDays(nursedeployments, currentnursedeployment, careFrom, type);
                        if (!result.isValid) {
                            return this.createError({ message: result.message });
                        }
                        return true;
                    }
                ),
            }),
        careTill: Yup.date()
            .nullable()
            .min(Yup.ref('careFrom'), 'Darf nicht vor Beginn liegen')
            .max(Yup.ref('customerContractEndAt'), 'Darf nicht nach Vertragsende liegen')
            .required('Pflichtfeld')
            .test(
                'not-fixated',
                fixationMessage,
                testNotNewlyFixated(currentnursedeployment?.careTill)
            ),
        notes: Yup.string(),
        arrivalAt: Yup.date().nullable(),
        departureAt: Yup.date().nullable().min(Yup.ref('arrivalAt'), 'Darf nicht vor Ankunft liegen'),
        withoutArrTransfer: Yup.boolean().required('Pflichtfeld'),
        withoutDepTransfer: Yup.boolean().required('Pflichtfeld'),
        a1From: Yup.date().nullable(),
        a1Till: Yup.date()
            .nullable()
            .when('a1From', {
                is: (a1From) => !!a1From,
                then: Yup.date()
                    .nullable()
                    .required('Pflichtfeld')
                    .when('a1From', (a1From, schema) =>
                        schema.test(
                            'compareDates',
                            'Datum darf nicht vor Beginn sein',
                            (a1Till) =>
                                a1Till === null ||
                                !moment(a1Till).startOf('date').isBefore(moment(a1From).startOf('date'))
                        )
                    ),
            }),
        billingFrom: Yup.date().when('type', {
            is: (type) =>
                type === NURSE_DEPLOYMENT_TYPE_PAUSE || type === NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED,
            then: Yup.date().nullable(),
            otherwise: Yup.date()
                .typeError('Muss ein gültiges Datum sein')
                .required('Pflichtfeld')
                .min(Yup.ref('arrivalAt'), 'Darf nicht vor Ankunft liegen')
                .test(
                    'is-date-in-use',
                    function (value) {
                        const { type, billingFrom } = this.parent;
                        const result = checkLegalGapDays(nursedeployments, currentnursedeployment, billingFrom, type);
                        if (!result.isValid) {
                            return this.createError({ message: result.message });
                        }
                        return true;
                    }
                ),
        })
            .test(
                'not-fixated',
                fixationMessage,
                testNotNewlyFixated(currentnursedeployment?.billingFrom)
            ),

        billingFromHalf: Yup.boolean().required('Pflichtfeld'),

        billingTill: Yup.date()
            .when('type', {
                is: (type) =>
                    type === NURSE_DEPLOYMENT_TYPE_PAUSE || type === NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED,
                then: Yup.date().nullable(),
                otherwise: Yup.date()
                    .typeError('Muss ein gültiges Datum sein')
                    .required('Pflichtfeld')
                    .min(Yup.ref('billingFrom'), 'Darf nicht vor Berechnung von liegen'),
            })
            .test(
                'not-fixated',
                fixationMessage,
                testNotNewlyFixated(currentnursedeployment?.billingTill)
            ),

        billingTillHalf: Yup.boolean().required('Pflichtfeld'),
        priceAgency: Yup.number(),
        priceAgencyDaily: Yup.string().required('Pflichtfeld'),
        surchargeAgency: Yup.number().max(
            Yup.ref('priceAgency'),
            'Darf nicht Preis Agentur übersteigen'
        ),
        a1Notes: Yup.string(),
        provisionPZH: Yup.number().max(Yup.ref('priceAgency'), 'Darf nicht Preis Agentur übersteigen'),
        surchargePZH: Yup.number().max(Yup.ref('provisionPZH'), 'Darf nicht Provision PZH übersteigen'),
        provisionLocation: Yup.number().max(
            Yup.ref('provisionPZH'),
            'Darf nicht Provision PZH übersteigen'
        ),
        provisionMultiplier: Yup.number().max(
            Yup.ref('provisionPZH'),
            'Darf nicht Provision PZH übersteigen'
        ),
    })
};
